import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage, restaurantForms } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { NullableInput } from '@/components/ui+/NullableInput';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

export const Route = createFileRoute('/r/$restaurantId/s/general-settings')({
  component: Component,
});

type GeneralSettings = z.output<typeof restaurantForms.generalSettings>;

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();

  const {
    data: restaurant,
    isError,
    isPending,
  } = useQuery(api.restaurants.getById, { restaurantId });

  const form = useForm({
    resolver: zodResolver(restaurantForms.generalSettings),
  });

  // Subscribe to changes
  useEffect(() => {
    if (!restaurant) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(restaurant, { keepDirty: true, keepDirtyValues: true });
  }, [restaurant]);

  const onValidSubmit = async (generalSettings: GeneralSettings) => {
    try {
      await convex.mutation(api.restaurants.saveGeneralSettings, {
        generalSettings,
        restaurantId,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  if (isPending) return <LoadingIcon />;
  if (isError) return null; // Handled by parent

  return (
    <Card>
      <CardHeader>
        <CardTitle>General Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            id="form"
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
            className="grid gap-6"
          >
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address */}
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Postcode */}
            <FormField
              control={form.control}
              name="postcode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postcode</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Phone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone number</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormDescription>
                    Phone number is shown on the website for customers to contact you
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="abn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ABN</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timeZone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timezone</FormLabel>
                  <FormControl>
                    <NullableInput {...field} disabled={true} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Customer Banner */}
            <FormField
              control={form.control}
              name="customerBannerText"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer banner message</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormDescription>
                    Banner message shown to customers on the website and mobile app
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormWrapper>
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={form.formState.isSubmitting || !form.formState.isDirty}
          loading={form.formState.isSubmitting}
          type="submit"
          form="form"
        >
          Save
        </ActionButton>
      </CardFooter>
    </Card>
  );
}
